import { useEffect, useRef } from 'react'
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'

const apiURL = process.env.REACT_APP_API_URL

// Delays between attempts at the very first connection. withAutomaticReconnect
// only covers a connection that opened and later dropped, so without this a
// hiccup at page load would leave live updates off for the whole session.
const INITIAL_RETRY_DELAYS_MS = [2000, 5000, 15000, 30000]

/**
 * Subscribes to diary changes made by other users and calls `onChange` whenever
 * the schedule moves. The API only pushes a signal, not the appointment itself -
 * every diary has its own employee/department filters, so the page has to
 * re-query to get the set of events that is correct for it.
 *
 * The callback is held in a ref so that passing a new closure on each render
 * does not tear down and rebuild the connection.
 */
export const useDiaryLiveUpdates = (onChange: () => void) => {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    const token = localStorage.getItem('authToken')

    // Nothing to subscribe to if the user isn't signed in; the page's own
    // fetches would be failing too.
    if (!token) return

    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`https://${apiURL}/hubs/diary`, {
        // Sent as an Authorization header where the transport allows it, and as
        // an access_token query parameter on the WebSocket handshake, which
        // cannot carry headers. The API accepts the query form for /hubs only.
        accessTokenFactory: () => localStorage.getItem('authToken') ?? '',
        // The API's CORS policy is AllowAnyOrigin, which browsers reject in
        // combination with credentials. We authenticate by bearer token rather
        // than cookies, so opting out keeps that policy usable as-is.
        withCredentials: false,
      })
      // Backs off and keeps retrying, so a laptop waking from sleep or a brief
      // network drop reconnects on its own.
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000])
      .configureLogging(LogLevel.Warning)
      .build()

    connection.on('DiaryChanged', () => {
      onChangeRef.current()
    })

    // A reconnect means we were disconnected for a while and will have missed
    // any broadcasts sent in the gap, so re-sync rather than trusting what is
    // currently on screen.
    connection.onreconnected(() => {
      onChangeRef.current()
    })

    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    const connect = (attempt = 0) => {
      if (cancelled) return

      connection.start().catch((error) => {
        if (cancelled) return

        const delay = INITIAL_RETRY_DELAYS_MS[attempt]

        if (delay === undefined) {
          // Live updates are an enhancement, not a requirement - the diary still
          // works, it just needs a manual refresh to show other users' changes.
          console.error('Diary live updates unavailable:', error)
          return
        }

        retryTimer = setTimeout(() => connect(attempt + 1), delay)
      })
    }

    connect()

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
      connection.stop().catch(() => {
        // Already closing or closed; nothing useful to do on unmount.
      })
    }
  }, [])
}
